import { Component, ElementRef, EventEmitter, inject, Input, Output, ViewChild } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { GlobalFloorplanService } from "@domain/project/floorplan/global-floorplan.service";
import { ProjectService } from "@domain/project/project.service";
import { TranslateFloorplanTagPipe } from "@domain/project/translate-floorplan-tag.pipe";
import { UserService } from "@domain/user/user.service";
import { BusinessScope } from "@lib/constants/business-scope";
import { AutocompleteSearchFilterPipe } from "@odx/angular/components/autocomplete";
import { User } from "@sentry/angular";
import { filter, map, Observable, switchMap } from "rxjs";

@Component({
  selector: "app-project-detail-file-card",
  templateUrl: "./project-detail-file-card.component.html",
  styleUrls: ["./project-detail-file-card.component.scss"],
  imports: [UiKitModule, ReactiveFormsModule, TranslateFloorplanTagPipe, AutocompleteSearchFilterPipe],
  standalone: true,
})
export class ProjectDetailFileCardComponent {
  private formBuilder = inject(FormBuilder);
  private userService = inject(UserService);
  private projectService = inject(ProjectService);
  private floorplanTagService = inject(GlobalFloorplanService);

  @Input() fileUrl!: string;
  @Input() fileName!: string;
  @Input() fileType?: string;
  @Input() description?: string;
  @Input() tags?: string[];

  @Output() delete$ = new EventEmitter();
  @Output() download$ = new EventEmitter();
  @Output() submit$ = new EventEmitter<FormGroup>();
  @Output() click$ = new EventEmitter();

  @ViewChild("submitButton")
  submitButton?: ElementRef;

  editForm!: FormGroup;
  editEnabled = false;

  protected user!: User;
  protected isReadOnlyUser$: Observable<boolean>;
  protected hasWritePermission$: Observable<boolean | undefined>;
  protected tagList$: Observable<string[]>;

  protected iconList = [
    { type: "application/pdf", icon: "file-pdf" },
    { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", icon: "file-excel" },
    { type: "text/csv", icon: "file-csv" },
    { type: "application/vnd.ms-powerpoint", icon: "file-ppt" },
    { type: "video/x-ms-wmv", icon: "file-wmv" },
    { type: "application/msword", icon: "file-word" },
    { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document", icon: "file-word" },
    { type: "application/x-zip-compressed", icon: "file-zip" },
    { type: "application/zip", icon: "file-zip" },
    { type: "application/zip-compressed", icon: "file-zip" },
    { type: "image/png", icon: "file-png" },
    { type: "image/jpeg", icon: "file-jpg" },
    { type: "image/svg+xml", icon: "file-svg" },
  ];

  businessScope$ = this.userService.businessScope$;

  constructor() {
    const formBuilder = this.formBuilder;

    this.isReadOnlyUser$ = this.userService.user$.pipe(map((user) => user.readOnlyAccess));
    this.hasWritePermission$ = this.projectService.hasUserWritePermission$.pipe(
      filter((val): val is boolean => val !== undefined),
    );
    this.editForm = formBuilder.group({
      name: [null, Validators.required],
      description: [""],
      tags: new FormControl<string[] | null>(null),
    });

    this.tagList$ = this.userService.businessScope$.pipe(switchMap((scope) => this.floorplanTagService.getFloorplanTags(scope)));
  }

  onEdit() {
    this.hasWritePermission$.subscribe((hasPermission) => {
      if (hasPermission) {
        this.editForm.patchValue({ name: this.fileName, tags: this.tags, description: this.description });
        this.editEnabled = true;
      }
    });
  }

  onDelete() {
    this.hasWritePermission$.subscribe((hasPermission) => {
      if (hasPermission) this.delete$.emit();
    });
  }

  onSubmit() {
    if (!this.editForm.valid) {
      return;
    }
    this.hasWritePermission$.subscribe((hasPermission) => {
      if (hasPermission) {
        this.submit$.emit(this.editForm);
        this.editEnabled = false;
      }
    });
  }

  onClick() {
    this.click$.emit();
  }

  onDownload() {
    this.download$.emit();
  }

  cancelEditMode() {
    this.editEnabled = false;
  }

  protected setIcon() {
    const filteredType = this.iconList.find((item) => item.type === this.fileType);
    return filteredType !== undefined ? filteredType.icon : "file-others";
  }

  protected readonly BusinessScope = BusinessScope;
}
